import { typedApi } from "@/api/typed-client";
import { ErrorState } from "@/components/ui/error-state";
import { PageHeaderV2 } from "@/components/ui/page-header-v2";
import { TableSkeleton } from "@/components/ui/skeleton-v2";
import { useDataset } from "@/features/datasets/queries";
import { useModels } from "@/features/models/queries";
import { useCurrentProject } from "@/features/projects/current-project";
import { useMutation } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { runEda, useAsyncResult } from "./queries";
import { ResultRenderer } from "./result-renderer";
import { RunPanel } from "./run-panel";

export function EdaPage() {
  const { datasetId } = useParams({ from: "/datasets/$datasetId/eda" });
  const { projectId } = useCurrentProject();
  const { data: dataset, isLoading, isError, refetch } = useDataset(datasetId);
  const { data: modelsPage } = useModels(projectId);
  const navigate = useNavigate();

  const [asyncState, setAsyncState] = useState<{ jobId: string; resultId: string } | null>(null);
  const [modelLabels, setModelLabels] = useState<Record<string, string>>({});
  const result = useAsyncResult(asyncState?.jobId ?? null, asyncState?.resultId ?? null);

  const mutation = useMutation({
    mutationFn: runEda,
    onSuccess: (data) => {
      setAsyncState({ jobId: data.job_id, resultId: data.result_id });
    },
  });

  if (isLoading) {
    return (
      <div className="pt-2">
        <TableSkeleton rows={3} cols={4} />
      </div>
    );
  }

  if (isError || !dataset) {
    return (
      <div className="pt-2">
        <ErrorState message="Dataset not found" onRetry={() => refetch()} />
      </div>
    );
  }

  const columns = dataset.schema?.map((col) => col.name) ?? [];
  const models =
    modelsPage?.items
      .filter((m) => m.status === "active")
      .map((m) => ({
        id: m.id,
        name: m.name,
        task_type: m.task_type,
        active_revision_no: 1,
        label: modelLabels[m.id],
      })) ?? [];

  const handleModelSelect = async (id: string) => {
    if (modelLabels[id]) return;
    try {
      const { data } = await typedApi.GET("/v1/models/{model_id}", {
        params: { path: { model_id: id }, query: { include_details: true } },
      });
      // Justified: model GET response is {[key:string]:unknown} in openapi schema
      const detail = data as unknown as {
        active_revision?: { training_summary?: { label?: string } };
      };
      const lbl = detail?.active_revision?.training_summary?.label;
      if (lbl) setModelLabels((prev) => ({ ...prev, [id]: lbl }));
    } catch {}
  };

  const handleSubmit = (params: {
    analyses: string[];
    label?: string;
    model_id?: string;
    revision_no?: number;
  }) => {
    mutation.mutate({
      project_id: projectId ?? "",
      dataset_id: datasetId,
      ...params,
    });
  };

  const isIdle = result.status === "idle";
  const isDone = result.status === "done";
  const isFailed = result.status === "failed";
  const isPolling = result.status === "queued" || result.status === "running";

  return (
    <>
      <PageHeaderV2
        eyebrow="Data · EDA"
        title={dataset.name}
        meta={
          <span className="flex items-center gap-3">
            <span>{dataset.kind}</span>
            {dataset.row_count != null && <span>{dataset.row_count.toLocaleString()} rows</span>}
            {dataset.column_count != null && <span>{dataset.column_count} cols</span>}
          </span>
        }
        action={
          <button
            type="button"
            onClick={() => navigate({ to: `/datasets/${datasetId}` })}
            className="rounded-md border border-line px-3 py-2 text-xs font-medium text-muted hover:text-ink"
          >
            <ArrowLeft size={14} className="mr-1 inline" />
            Back
          </button>
        }
      />

      {/* Show RunPanel when idle or after done/failed (for rerun) */}
      {(isIdle || isDone || isFailed) && (
        <div className="mb-6">
          <RunPanel
            onSubmit={handleSubmit}
            isSubmitting={mutation.isPending}
            columns={columns}
            models={models}
            onModelSelect={handleModelSelect}
          />
        </div>
      )}

      {/* Polling spinner */}
      {isPolling && (
        <div className="mb-6 flex items-center gap-3 rounded-[14px] border border-line bg-surface p-5">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-teal-500 border-t-transparent" />
          <p className="text-sm text-muted">
            {result.status === "queued"
              ? "Queued…"
              : `Running${result.progress != null ? ` (${result.progress}%)` : ""}…`}
          </p>
        </div>
      )}

      {/* Error state */}
      {isFailed && (
        <div className="mb-6 rounded-[14px] border border-rose-200 bg-rose-50 p-5 dark:border-rose-800 dark:bg-rose-900/20">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-rose-700 dark:text-rose-300">
            Analysis Failed
          </p>
          <p className="mt-2 text-sm text-rose-800 dark:text-rose-200">
            {result.error ?? "Unknown error"}
          </p>
        </div>
      )}

      {/* Results */}
      {isDone && result.result && (
        <div className="mb-6">
          <ResultRenderer analyses={result.result.analyses} />
        </div>
      )}
    </>
  );
}
